import { useEffect, useMemo, useState } from "react";
import { doGET } from "./HttpUtil";

const DOC_TYPE_SCOPE = {
  BUSINESS: "business",
  INDIVIDUAL: "individual",
};


const formatWithContext = (template, context = {}) => {
  if (typeof template === "function") return template(context);
  if (typeof template !== "string") return template;

  const displayName = context.displayName || "this individual";
  return template.replace(/{displayName}/g, displayName);
};

const buildMetaFromRecord = (record = {}, context = {}) => {
  const labelTemplate = record?.label || (record?.key || record?._id);
  const errorTemplate = record?.errorMsg || labelTemplate;

  const label = formatWithContext(labelTemplate, context);
  const errorMessage = formatWithContext(errorTemplate, context);

  return {
    scope: record?.forBusiness ? DOC_TYPE_SCOPE.BUSINESS : DOC_TYPE_SCOPE.INDIVIDUAL,
    type: record?.key || record?._id,
    field: record?.key || record?._id,
    showCategory: record?.key || record?._id,
    supportedFiles: record?.fileExtensions?.length ? record.fileExtensions : ["image", "pdf"],
    validateByAi: Boolean(record?.isParchaRequired),
    single: true,
    label,
    errorMessage,
    dataTestId: `doc-${record?.key || record?._id}`,
  };
};

export const resolveDocTypeMeta = (docType, { context = {}, docTypeMap } = {}) => {
  const record =
    docTypeMap?.[docType] ||
    docTypeMap?.[String(docType || "").trim()] ||
    docTypeMap?.[(docType)];

  if (record) {
    return buildMetaFromRecord(record, context);
  }

  const label = formatWithContext((docType), context);
  return {
    scope: DOC_TYPE_SCOPE.BUSINESS,
    type: docType,
    field: docType,
    showCategory: docType,
    supportedFiles: ["image", "pdf"],
    validateByAi: false,
    single: true,
    label,
    errorMessage: label,
    dataTestId: `doc-${docType}`,
  };
};

export const splitDocTypesByScope = (docTypes = [], docTypeMap = {}) =>
  (docTypes || []).reduce(
    (acc, docType) => {
      const meta = resolveDocTypeMeta(docType, { docTypeMap });
      if (meta.scope === DOC_TYPE_SCOPE.INDIVIDUAL) {
        acc.individual.push(docType);
      } else {
        acc.business.push(docType);
      }
      return acc;
    },
    { business: [], individual: [] },
  );

export const useDocTypes = () => {
  const [docTypeMap, setDocTypeMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchDocTypes = async () => {
      setLoading(true);
      try {
        const response = await doGET(`/api/doc-type/grid?rows=-1`);
        const rows = response?.data?.rows || [];
        if (!isMounted) return;
        const map = rows.reduce((acc, row) => {
          const key = row?.key || row?._id;
          if (key) {
            acc[key] = row;
          }
          return acc;
        }, {});
        setDocTypeMap(map);
      } catch (err) {
        if (isMounted) setError(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchDocTypes();
    return () => {
      isMounted = false;
    };
  }, []);

  const getMeta = useMemo(
    () => (docType, ctx = {}) => resolveDocTypeMeta(docType, { context: ctx, docTypeMap }),
    [docTypeMap],
  );

  return { docTypeMap, getMeta, loading, error };
};

export { DOC_TYPE_SCOPE };
